<template>
	<div class="color-picker-wrapper">
		<label class="color-picker-title b360-form-label">{{ title }}</label>
		<div
			v-if="!disabled"
			class="color-picker"
			ref="colorpicker"
			@focus="showPicker"
			@focusout="hidePicker"
			tabindex="0"
			:aria-label="`click or focus to change the color for the ${title}`"
		>
			<span class="color-picker-container">
				<span
					class="color-swatch"
					:style="`background-color: ${colorValue}`"
				></span>
				<ChromePicker
					:value="colors"
					@input="updateFromPicker"
					v-if="displayPicker"
				/>
			</span>
			<input
				type="text"
				class="color-name-container"
				v-model="colorValue"
				@input="updateFromInput"
				@focus="showPicker"
			/>
		</div>
		<div
			v-else
			class="color-picker"
			ref="colorpicker"
			tabindex="0"
			:aria-label="`click or focus to change the color for the ${title}`"
		>
			<span class="color-picker-container">
				<span
					class="color-swatch"
					:style="`background-color: ${colorValue}`"
				></span>
				<ChromePicker
					:value="colors"
					@input="updateFromPicker"
					v-if="displayPicker"
				/>
			</span>
			<input
				:disabled="disabled"
				type="text"
				class="color-name-container"
				v-model="colorValue"
				@input="updateFromInput"
				@focus="showPicker"
			/>
		</div>
	</div>
</template>

<script>
/* eslint-disable no-bitwise, radix */

import { Chrome } from 'vue-color';

export default {
	name: 'ColorPicker',
	components: {
		ChromePicker: Chrome,
	},
	props: {
		color: String,
		title: String,
		name: String,
		disabled: Boolean,
	},
	data() {
		return {
			colors: {
				hex: '#000000',
			},
			colorValue: '',
			displayPicker: false,
		};
	},
	mounted() {
		this.setColor(this.color || '#000000');
	},
	methods: {
		setColor(color) {
			if (color) {
				this.updateColors(color);
				this.colorValue = color;
				this.$emit('input', {
					fieldName: this.name,
					value: color,
				});
			}
		},
		updateColors(color) {
			if (color.slice(0, 1) === '#') {
				this.colors = {
					hex: color,
				};
			} else if (color.slice(0, 4) === 'rgba') {
				const rgba = color.replace(/^rgba?\(|\s+|\)$/g, '').split(',');
				const hex = `#${(
					(1 << 24) +
					(parseInt(rgba[0]) << 16) +
					(parseInt(rgba[1]) << 8) +
					parseInt(rgba[2])
				)
					.toString(16)
					.slice(1)}`;
				this.colors = {
					hex,
					a: rgba[3],
				};
			}
		},
		showPicker() {
			this.displayPicker = true;
		},
		hidePicker() {
			this.displayPicker = false;
		},
		updateFromInput() {
			this.updateColors(this.colorValue);
		},
		updateFromPicker(color) {
			this.colors = color;
			if (color.rgba.a === 1) {
				this.colorValue = color.hex;
			} else {
				this.colorValue = `rgba(${color.rgba.r}, ${color.rgba.g}, ${color.rgba.b}, ${color.rgba.a})`;
			}
		},
		documentClick(e) {
			const el = this.$refs.colorpicker;
			const { target } = e;
			if (el !== target && !el.contains(target)) {
				this.hidePicker();
			}
		},
	},
	watch: {
		colorValue(val) {
			this.setColor(val);
		},
		color(val) {
			this.setColor(val);
		},
	},
};
</script>

<style lang="scss" scoped>
.color-picker-wrapper {
	display: flex;
	flex-direction: column;
}

.color-picker-title {
	display: inline-block;
	@include rem('margin-bottom', 10px);
}

.color-picker {
	position: relative;
	@include rem('padding', 6px 7px);
	border-radius: $b360-border-radius-base;
	border: $b360-border-width-thin solid $border-color-light;
	line-height: 0;
	display: grid;
	grid-template-columns: 1fr 6fr;
}

.color-swatch {
	display: inline-block;
	border-radius: $b360-border-radius-base;
	border: $b360-border-width-thin solid $border-color-light;
	@include rem('width', 28px);
	@include rem('height', 28px);
	cursor: pointer;
}

.vc-chrome {
	position: absolute;
	top: 100%;
	left: 0;
	max-width: 100%;
	z-index: 10;
}

.color-name-container {
	text-align: center;
	border: none;
	width: 100%;
	max-width: 100%;
	@include typo--roboto__medium;
	color: $subheader-color;
	@include rem(font-size, 16px);
}
</style>
